import React, { useContext, useEffect, useState } from 'react';
import { Spinner } from '@amzn/awsui-components-react/polaris';
import StringUtils from 'src/utils/stringUtils';
import { SegmentTablesContext } from 'src/components/segment-cdt/SegmentTablesContext';
import { segmentCoaName } from 'src/components/segment-cdt/create/segmentCreateUtils';

/**
 * The table's segment population, in the detail view's metadata row.
 *
 * <p>`SegmentTableDefinition` carries only `segmentCustomCoaId` — a uuid — so the name has to be resolved
 * here, out of the same list the create wizard picks from (`GetActiveCustomCOADefinitionsForSegment`, keyed
 * by the table's segment). The request is made only when there is an id to resolve: most tables have no
 * population restriction, and a null argument leaves the hook idle rather than fetching an answer that would
 * be discarded.
 *
 * <p><b>Falls back to the id rather than inventing a name.</b> That list holds ACTIVE definitions only, so a
 * table pointing at an archived or deleted Custom CoA has no name to show, and neither does one whose segment
 * the Custom CoA API does not cover. The id is then the only true thing available and is shown as itself, with
 * a tooltip saying why. The fallback is reached only once the lookup has settled, so the uuid never flashes
 * on the way to the name.
 */
export default function SegmentPopulationLabel() {
    const { state, services } = useContext(SegmentTablesContext);
    const def = state?.detail?.definition;
    const coaId = def?.segmentCustomCoaId;
    // Null on both counts that make a lookup pointless: no id to resolve, or a segment the Custom CoA API has
    // no name for. Either way the hook stays idle, which is also what keeps `resolving` below from waiting on
    // a request that will never be made.
    const coaSegment = coaId ? segmentCoaName(def?.segmentType) : null;
    const [definitions, loading, error] = services.customCOAService.getCustomCOADefinitionsForSegment(coaSegment);

    // The hook reports `loading` from an effect, so its first render is indistinguishable from "settled and
    // found nothing" — and rendering the fallback then would flash the uuid the name is about to replace.
    // Remembering *which* segment a request was seen for is what tells the two apart, and keeps telling them
    // apart when the segment changes under a mounted component: the hook re-fetches on a new `coaSegment`, and
    // there is a render where the segment is already the new one but its request has not been reported yet.
    const [seenFor, setSeenFor] = useState<string | null>(null);
    useEffect(() => {
        if (loading) {
            setSeenFor(coaSegment);
        }
    }, [loading, coaSegment]);

    useEffect(() => {
        // Warned, not bannered: this is one label on a page whose data loaded fine, and the id is still shown,
        // so a banner would overstate a failure the reader can do nothing about.
        if (!StringUtils.isNullOrEmpty(error)) {
            console.warn('Could not resolve the Custom CoA name for this segment table:', error);
        }
    }, [error]);

    // Settled means the lookup for *this* segment has come back — successfully or not. `error` is deliberately
    // not consulted: the hook never clears it, so a single failure would otherwise read as settled forever,
    // including across a later segment change. A failed lookup settles because its `loading` falls, which lands
    // on the fallback below, which is what a failure should show anyway.
    const resolving = !!coaSegment && (loading || seenFor !== coaSegment);

    return (
        <span>
            <strong>Segment Population (Custom CoA):</strong>{' '}
            {value(!!def, coaId, definitions, resolving)}
        </span>
    );
}

/** What follows the label: the resolved name, a spinner while resolving, the bare id, or "None". */
function value(
    hasDefinition: boolean,
    coaId: string | undefined,
    definitions: { tableId: string; tableName: string }[],
    resolving: boolean,
) {
    if (!coaId) {
        // "None" is a statement about the table, so it waits for the table: until the definition lands there
        // is nothing known to report either way.
        return hasDefinition ? 'None' : null;
    }
    const match = (definitions ?? []).find((d) => d.tableId === coaId);
    if (match?.tableName) {
        return match.tableName;
    }
    if (resolving) {
        return <Spinner />;
    }
    return (
        <span title="This Custom CoA definition is not active, so its name could not be resolved.">
            {coaId}
        </span>
    );
}
